import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import BrandGrid from '../components/BrandGrid';
import Header from '../components/Header';
import api from '../api/axios';
import { useLocalCart } from '../context/CartContext';
import './LandingPage.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n) =>
    n
        ? new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              maximumFractionDigits: 0,
          }).format(n)
        : '';

const clamp = (str, len) =>
    str && str.length > len ? str.slice(0, len).trimEnd() + '…' : str;

// ─── Category icon + colour system ───────────────────────────────────────────

/**
 * 10-color pastel palette. Assigned deterministically by hashing the
 * category name so the same category always gets the same color.
 */
const PALETTE = [
    { accent: '#60A5FA' }, // Blue
    { accent: '#34D399' }, // Emerald
    { accent: '#FB923C' }, // Orange
    { accent: '#A78BFA' }, // Violet
    { accent: '#FB7185' }, // Rose
    { accent: '#2DD4BF' }, // Teal
    { accent: '#FBBF24' }, // Amber
    { accent: '#E879F9' }, // Fuchsia
    { accent: '#38BDF8' }, // Sky
    { accent: '#F87171' }, // Red
];

/** Emoji icons keyed by lowercase keyword substrings. */
const ICON_MAP = {
    electronic: '💻', laptop: '💻', computer: '🖥️', monitor: '🖥️',
    mobile: '📱', phone: '📱', tablet: '📱',
    cloth: '👕', apparel: '👗', fashion: '👔', shirt: '👕', wear: '👔',
    office: '📎', stationery: '✏️', pen: '🖊️', notebook: '📓', paper: '📄',
    food: '🍕', snack: '🍿', beverage: '☕', coffee: '☕', drink: '🥤',
    sport: '⚽', fitness: '🏋️', gym: '🏋️',
    bag: '🎒', backpack: '🎒', luggage: '🧳',
    gift: '🎁', hamper: '🎁',
    accessori: '⌚', watch: '⌚', jewel: '💍',
    tech: '📱', gadget: '🔌', charger: '🔌', cable: '🔌', usb: '💾',
    home: '🏠', decor: '🖼️', kitchen: '🍳',
    book: '📚', education: '🎓',
    toy: '🧸', game: '🎮',
    brand: '🏷️', merch: '🛍️', merchandise: '🛍️', print: '🖨️',
    drinkware: '🥤', mug: '☕', bottle: '🍶',
    headwear: '🧢', cap: '🧢', hat: '🎩',
    umbrella: '☂️',
    wellness: '🧘', health: '💊', medic: '💊',
    travel: '✈️', outdoor: '⛺',
    tool: '🔧', equipment: '🔧',
    plant: '🌱', nature: '🌿',
    premium: '✨', luxury: '💎', award: '🏆', trophy: '🏆',
};

const hashStr = (str) => {
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h) + str.charCodeAt(i);
    return Math.abs(h);
};

const getCategoryTheme = (name) => PALETTE[hashStr(name) % PALETTE.length];

const getCategoryIcon = (name) => {
    const lower = name.toLowerCase();
    for (const [key, icon] of Object.entries(ICON_MAP)) {
        if (lower.includes(key)) return icon;
    }
    return '📦';
};

// ─── Skeleton ────────────────────────────────────────────────────────────────

const Skeleton = ({ className, style }) => (
    <div className={`lp-skeleton ${className || ''}`} style={style} aria-hidden="true" />
);

// ─── Quick View Modal ────────────────────────────────────────────────────────

const QuickViewModal = ({ product, onClose, navigate }) => {
    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    if (!product) return null;

    const offerPrice   = product.price;
    const mrp          = product.mrp;
    const displayPrice = offerPrice || mrp;
    const hasDisc      = offerPrice && mrp && offerPrice < mrp;
    const discount     = hasDisc ? Math.round(((mrp - offerPrice) / mrp) * 100) : 0;

    return (
        <div className="lp-qv-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={`Quick view: ${product.name}`}>
            <div className="lp-qv-modal" onClick={(e) => e.stopPropagation()}>
                <button className="lp-qv-close" onClick={onClose} aria-label="Close quick view">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
                <div className="lp-qv-body">
                    <div className="lp-qv-img-wrap">
                        {product.image
                            ? <img src={product.image} alt={product.name} />
                            : <div className="lp-qv-img-placeholder" aria-hidden="true">📦</div>}
                    </div>
                    <div className="lp-qv-info">
                        {product.category && <span className="lp-qv-category">{product.category}</span>}
                        <h3 className="lp-qv-name">{product.name}</h3>
                        <div className="lp-qv-price-row">
                            <span className="lp-qv-price">{fmt(displayPrice)}</span>
                            {hasDisc && (
                                <>
                                    <span className="lp-qv-mrp">{fmt(mrp)}</span>
                                    <span className="lp-qv-discount">{discount}% off</span>
                                </>
                            )}
                        </div>
                        {product.description && (
                            <p className="lp-qv-desc">{clamp(product.description, 160)}</p>
                        )}
                        <div className="lp-qv-actions">
                            <button className="lp-qv-btn lp-qv-btn--primary" onClick={() => { navigate(`/product/${product.id}`); onClose(); }}>
                                View Full Details
                            </button>
                            <button className="lp-qv-btn lp-qv-btn--ghost" onClick={() => { navigate('/products'); onClose(); }}>
                                Browse All Products
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Category Grid ────────────────────────────────────────────────────────────

const CategoryGrid = ({ categories, isLoading, navigate }) => {
    const [expanded, setExpanded] = useState(false);
    const skeletonCount = 10;

    return (
        <section className="lp-cat-section" aria-label="Browse by category">
            <div className="lp-cat-section__inner">
                <div className="lp-cat-section__header">
                    <h2 className="lp-cat-section__title">Shop by Category</h2>
                    <button className="lp-cat-section__view-all" onClick={() => navigate('/products')}>
                        View all →
                    </button>
                </div>

                <div className={`lp-cat-grid-wrap${expanded ? '' : ' lp-cat-grid-wrap--collapsed'}`}>
                    <div className="lp-cat-grid">
                        {isLoading
                            ? Array.from({ length: skeletonCount }).map((_, i) => (
                                  <div key={i} className="lp-cat-tile lp-cat-tile--skeleton" aria-hidden="true">
                                      <Skeleton className="lp-cat-tile__icon-wrap" style={{ height: '52px', borderRadius: '12px' }} />
                                      <Skeleton style={{ height: '12px', width: '68%', marginTop: '6px' }} />
                                  </div>
                              ))
                            : categories.map((cat) => {
                                  const theme = getCategoryTheme(cat.name);
                                  const icon  = getCategoryIcon(cat.name);
                                  return (
                                      <button
                                          key={cat.name}
                                          className="lp-cat-tile"
                                          style={{ '--cat-accent': theme.accent }}
                                          onClick={() => navigate(`/products?category=${encodeURIComponent(cat.name)}`)}
                                          aria-label={`Browse ${cat.name}`}
                                      >
                                          <div className="lp-cat-tile__icon-wrap" aria-hidden="true">
                                              <span className="lp-cat-tile__icon">{icon}</span>
                                          </div>
                                          <span className="lp-cat-tile__name">{cat.name}</span>
                                      </button>
                                  );
                              })}
                    </div>
                    {!expanded && !isLoading && <div className="lp-cat-fade" aria-hidden="true" />}
                </div>

                {!isLoading && categories.length > 0 && (
                    <button
                        className="lp-cat-expand-btn"
                        onClick={() => setExpanded(e => !e)}
                        aria-expanded={expanded}
                    >
                        {expanded ? 'Show less ↑' : `Show all ${categories.length} categories ↓`}
                    </button>
                )}
            </div>
        </section>
    );
};

// ─── Shelf product card ───────────────────────────────────────────────────────

const ShelfProductCard = ({ product, onQuickView, navigate }) => {
    const { addToCart } = useLocalCart();
    const [added, setAdded] = useState(false);

    const offerPrice = product.price;            // marketOperatingPriceWithGst (excl. GST base + GST)
    const mrp        = product.mrp;              // always incl. GST
    const displayPrice = offerPrice || mrp;      // fall back to MRP when no offer price
    const hasDisc    = offerPrice && mrp && offerPrice < mrp;
    const discount   = hasDisc ? Math.round(((mrp - offerPrice) / mrp) * 100) : 0;

    const handleAddToCart = (e) => {
        e.stopPropagation();
        addToCart(product);
        setAdded(true);
        setTimeout(() => setAdded(false), 1500);
    };

    return (
        <div className="lp-shelf-card" role="article" aria-label={product.name}>
            <div
                className="lp-shelf-card__img-wrap"
                onClick={() => onQuickView(product)}
                role="button"
                tabIndex={0}
                aria-label={`Quick view ${product.name}`}
                onKeyDown={(e) => e.key === 'Enter' && onQuickView(product)}
            >
                {product.image
                    ? <img src={product.image} alt={product.name} loading="lazy" />
                    : <div className="lp-shelf-card__img-placeholder" aria-hidden="true">📦</div>}
                {hasDisc && (
                    <span className="lp-shelf-card__discount-badge">{discount}% off</span>
                )}
            </div>
            <div className="lp-shelf-card__body">
                {product.category && (
                    <span className="lp-shelf-card__category">{product.category}</span>
                )}
                <p className="lp-shelf-card__name">{clamp(product.name, 52)}</p>
                <div className="lp-shelf-card__price-row">
                    <span className="lp-shelf-card__price">{fmt(displayPrice)}</span>
                    {hasDisc && <span className="lp-shelf-card__mrp">{fmt(mrp)}</span>}
                    <span className="lp-shelf-card__gst-tag">incl. GST</span>
                </div>
                <button
                    className={`lp-shelf-card__add-btn${added ? ' lp-shelf-card__add-btn--added' : ''}`}
                    onClick={handleAddToCart}
                    aria-label={`Add ${product.name} to cart`}
                >
                    {added ? '✓ Added' : '+ Add to Cart'}
                </button>
                <button
                    className="lp-shelf-card__cta"
                    onClick={() => navigate(`/product/${product.id}`)}
                    aria-label={`View details for ${product.name}`}
                >
                    View Details
                </button>
            </div>
        </div>
    );
};

const ShelfCardSkeleton = () => (
    <div className="lp-shelf-card lp-shelf-card--skeleton" aria-hidden="true">
        <Skeleton style={{ height: '180px', borderRadius: 'var(--radius-lg)' }} />
        <div className="lp-shelf-card__body">
            <Skeleton style={{ height: '11px', width: '55%', marginBottom: '8px' }} />
            <Skeleton style={{ height: '14px', width: '90%', marginBottom: '4px' }} />
            <Skeleton style={{ height: '14px', width: '65%', marginBottom: '12px' }} />
            <Skeleton style={{ height: '16px', width: '40%', marginBottom: '12px' }} />
            <Skeleton style={{ height: '32px', borderRadius: 'var(--radius-md)' }} />
        </div>
    </div>
);

// ─── Product shelf ────────────────────────────────────────────────────────────

const ProductShelf = ({ title, badge, products, isLoading, onQuickView, navigate, viewAllHref }) => {
    const scrollRef = React.useRef(null);

    const scroll = (dir) => {
        scrollRef.current?.scrollBy({ left: dir * 300, behavior: 'smooth' });
    };

    return (
        <section className="lp-shelf" aria-label={title}>
            <div className="lp-shelf__header">
                <div className="lp-shelf__title-group">
                    <h2 className="lp-shelf__title">{title}</h2>
                    {badge && <span className="lp-shelf__badge">{badge}</span>}
                </div>
                <button className="lp-shelf__view-all" onClick={() => navigate(viewAllHref || '/products')}>
                    View all →
                </button>
            </div>

            <div className="lp-shelf__scroll-wrap">
                <button className="lp-shelf__arrow lp-shelf__arrow--prev" onClick={() => scroll(-1)} aria-label={`Scroll ${title} left`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
                </button>

                <div className="lp-shelf__track" ref={scrollRef}>
                    {isLoading
                        ? Array.from({ length: 6 }).map((_, i) => <ShelfCardSkeleton key={i} />)
                        : products.map((p) => (
                              <ShelfProductCard key={p.id} product={p} onQuickView={onQuickView} navigate={navigate} />
                          ))}
                </div>

                <button className="lp-shelf__arrow lp-shelf__arrow--next" onClick={() => scroll(1)} aria-label={`Scroll ${title} right`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
            </div>
        </section>
    );
};

// ─── B2B Promo Banner ─────────────────────────────────────────────────────────

const B2BPromoBanner = ({ navigate }) => (
    <section className="lp-promo" aria-label="Bulk order offer">
        <div className="lp-promo__inner">
            <div className="lp-promo__left">
                <span className="lp-promo__eyebrow">Bulk Orders Welcome</span>
                <h2 className="lp-promo__title">Need Custom Branding on Your Products?</h2>
                <p className="lp-promo__sub">
                    We brand products with your company logo — perfect for corporate gifting,
                    employee onboarding kits, and event merchandise. Ships across India.
                </p>
                <div className="lp-promo__actions">
                    <button className="lp-promo__btn lp-promo__btn--primary" onClick={() => navigate('/products')}>
                        Browse Products
                    </button>
                    <button className="lp-promo__btn lp-promo__btn--ghost" onClick={() => navigate('/quote')}>
                        View My Quote
                    </button>
                </div>
            </div>
            <div className="lp-promo__stats" aria-hidden="true">
                <div className="lp-promo__stat"><span className="lp-promo__stat-num">500+</span><span className="lp-promo__stat-lbl">SKUs</span></div>
                <div className="lp-promo__stat"><span className="lp-promo__stat-num">4</span><span className="lp-promo__stat-lbl">Cities</span></div>
                <div className="lp-promo__stat"><span className="lp-promo__stat-num">100%</span><span className="lp-promo__stat-lbl">Custom Branding</span></div>
            </div>
        </div>
    </section>
);

// ─── How It Works ─────────────────────────────────────────────────────────────

const HOW_IT_WORKS_STEPS = [
    {
        num: '01',
        icon: '🔍',
        title: 'Browse & Shortlist',
        desc: 'Explore 1,000+ products across 20+ categories. Filter by budget, category, or brand.',
    },
    {
        num: '02',
        icon: '📋',
        title: 'Request a Quote',
        desc: 'Add items to your cart and submit with your branding requirements and quantity.',
    },
    {
        num: '03',
        icon: '🎁',
        title: 'Receive Custom Orders',
        desc: 'We brand, pack, and ship directly to your team or event venue — anywhere in India.',
    },
];

const HowItWorks = () => (
    <section className="lp-hiw" aria-label="How it works">
        <div className="lp-hiw__inner">
            <div className="lp-hiw__header">
                <h2 className="lp-hiw__title">How It Works</h2>
                <p className="lp-hiw__sub">Simple, fast, and tailored to your business needs.</p>
            </div>
            <div className="lp-hiw__steps">
                {HOW_IT_WORKS_STEPS.map((step) => (
                    <div key={step.num} className="lp-hiw__step">
                        <span className="lp-hiw__step-num">{step.num}</span>
                        <span className="lp-hiw__step-icon" aria-hidden="true">{step.icon}</span>
                        <h3 className="lp-hiw__step-title">{step.title}</h3>
                        <p className="lp-hiw__step-desc">{step.desc}</p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

// ─── Trust Bar ────────────────────────────────────────────────────────────────

const TRUST_ITEMS = [
    {
        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
        title: 'Pan-India Delivery',
        sub: 'We ship to every corner of India',
    },
    {
        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>,
        title: 'Bulk Order Pricing',
        sub: 'Special rates for high-volume orders',
    },
    {
        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
        title: 'Custom Branding',
        sub: 'Your logo on 500+ products',
    },
    {
        icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
        title: 'Dedicated Account Manager',
        sub: 'Expert support for enterprise clients',
    },
];

const TrustBar = () => (
    <section className="lp-trust" aria-label="Why choose eYantra Commerce">
        <div className="lp-trust__inner">
            {TRUST_ITEMS.map((item, i) => (
                <div key={i} className="lp-trust__item">
                    <div className="lp-trust__icon">{item.icon}</div>
                    <div className="lp-trust__text">
                        <strong className="lp-trust__title">{item.title}</strong>
                        <span className="lp-trust__sub">{item.sub}</span>
                    </div>
                </div>
            ))}
        </div>
    </section>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const LandingPage = () => {
    const navigate = useNavigate();

    const [state, setState] = useState({
        isLoading:      true,
        newArrivals:    [],
        trending:       [],
        categories:     [],
        totalProducts:  0,
        totalCategories: 0,
    });

    const [quickView, setQuickView] = useState(null);
    const [categoryPreviews, setCategoryPreviews] = useState([]);

    useEffect(() => {
        const load = async () => {
            try {
                const [prodsRes, catsRes] = await Promise.all([
                    api.get('/products?size=50&sort=id,desc').catch(() => ({ data: { content: [], totalElements: 0 } })),
                    api.get('/categories/top').catch(() => ({ data: [] })),
                ]);

                const rawProds      = prodsRes.data?.content || [];
                const totalProducts = prodsRes.data?.totalElements || 0;
                const rawCatsRaw    = catsRes.data || [];
                const rawCats       = rawCatsRaw.map((c) => (typeof c === 'string' ? { id: c, name: c } : c));

                const productsMapped = rawProds.map((p) => ({
                    id:          p.id,
                    name:        p.name,
                    image:       p.thumbnailUrl || p.imageUrl || null,
                    price:       p.marketOperatingPriceWithGst || null,
                    mrp:         p.mrp,
                    category:    p.category,
                    description: p.description,
                }));

                // Dynamic category counts
                const dynamicCounts = {};
                productsMapped.forEach((p) => {
                    if (p.category) dynamicCounts[p.category] = (dynamicCounts[p.category] || 0) + 1;
                });

                // Build category list with counts, sorted by count desc
                const categories = rawCats
                    .map((cat) => {
                        const backendCount = cat.products?.length || cat.productCount || cat.count || 0;
                        const count = backendCount > 0 ? backendCount : (dynamicCounts[cat.name] || 0);
                        return { name: cat.name, id: cat.id, count };
                    })
                    .sort((a, b) => b.count - a.count);

                setState({
                    isLoading:       false,
                    newArrivals:     productsMapped.slice(0, 10),
                    trending:        productsMapped.slice(10, 20),
                    categories,
                    totalProducts,
                    totalCategories: rawCats.length,
                });
            } catch {
                setState((s) => ({ ...s, isLoading: false }));
            }
        };
        load();
    }, []);

    useEffect(() => {
        api.get('/products/category-preview')
            .then(r => setCategoryPreviews(r.data || []))
            .catch(() => {});
    }, []);

    const { isLoading, newArrivals, trending, categories } = state;

    return (
        <div className="lp-page">
            <Header />

            <main>
                {/* 1. Category Grid — first visible section */}
                <CategoryGrid
                    categories={categories}
                    isLoading={isLoading}
                    navigate={navigate}
                />

                {/* 2. New Arrivals shelf */}
                <ProductShelf
                    title="New Arrivals"
                    badge="Just Added"
                    products={newArrivals}
                    isLoading={isLoading}
                    onQuickView={setQuickView}
                    navigate={navigate}
                    viewAllHref="/products?sort=id,desc"
                />

                {/* 3. B2B value proposition */}
                <B2BPromoBanner navigate={navigate} />

                {/* 4. Trending shelf */}
                <ProductShelf
                    title="Trending Now"
                    badge="Popular"
                    products={trending}
                    isLoading={isLoading}
                    onQuickView={setQuickView}
                    navigate={navigate}
                    viewAllHref="/products"
                />

                {/* 5. How it works */}
                <HowItWorks />

                {/* 6. Trust signals */}
                <TrustBar />

                {/* 7. Brand ticker */}
                <BrandGrid />

                {/* 8. Category preview rows — Amazon style */}
                {categoryPreviews.length > 0 && (
                    <section className="lp-cat-preview-section">
                        <h2 className="lp-cat-preview-section__title">Shop by Category</h2>
                        {categoryPreviews.map((preview) => (
                            <div key={preview.categoryName} className="lp-cat-preview-row">
                                <div className="lp-cat-preview-row__header">
                                    <div>
                                        <h3 className="lp-cat-preview-row__name">{preview.categoryName}</h3>
                                        <span className="lp-cat-preview-row__count">{preview.productCount} products</span>
                                    </div>
                                    <button
                                        className="lp-cat-preview-row__browse"
                                        onClick={() => navigate(`/products?category=${encodeURIComponent(preview.categoryName)}`)}
                                    >
                                        Browse all
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="14" height="14"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                                    </button>
                                </div>
                                <div className="lp-cat-preview-row__grid">
                                    {preview.sampleProducts.map((product) => (
                                        <div
                                            key={product.id}
                                            className="lp-cat-preview-card"
                                            onClick={() => navigate(`/product/${product.id}`)}
                                        >
                                            <div className="lp-cat-preview-card__img-wrap">
                                                <img
                                                    src={product.thumbnailUrl || product.imageUrl || 'https://placehold.co/200x200/F9FAFB/9CA3AF?text=No+Image'}
                                                    alt={product.name}
                                                    className="lp-cat-preview-card__img"
                                                />
                                            </div>
                                            <p className="lp-cat-preview-card__name">{product.name}</p>
                                            <p className="lp-cat-preview-card__price">
                                                {product.marketOperatingPriceWithGst
                                                    ? fmt(product.marketOperatingPriceWithGst)
                                                    : fmt(product.mrp)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                )}
            </main>

            {quickView && (
                <QuickViewModal
                    product={quickView}
                    onClose={() => setQuickView(null)}
                    navigate={navigate}
                />
            )}

            <footer style={{ borderTop: '1px solid #eee', padding: '16px 24px', textAlign: 'center', fontSize: 13, color: '#888' }}>
                © {new Date().getFullYear()} eYantra. &nbsp;
                <Link to="/privacy-policy" style={{ color: '#888', textDecoration: 'underline' }}>Privacy Policy</Link>
            </footer>
        </div>
    );
};

export default LandingPage;